import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, TrendingUp, TrendingDown, Target, Loader2, Award, ArrowRight,
  MessageSquare, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import MyGamificationCard from './MyGamificationCard';
import CoachingOutcomeTable from './CoachingOutcomeTable';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score Trend + Skill Breakdown (moved from Individual) and the
// Coaching → Revenue table (moved from Intelligence). Self-contained so
// the main tab keeps its existing data flow untouched.
const CoachingChartsBlock = ({ agentId, domainId }) => {
  const [indiv, setIndiv] = useState(null);
  const [outcomeTable, setOutcomeTable] = useState([]);
  useEffect(() => {
    if (!agentId) return;
    axios.get(`${API}/analytics/individual/${agentId}?days=90`, { timeout: 35000 })
      .then((r) => setIndiv(r.data)).catch(() => setIndiv(null));
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', '60');
    p.set('top_n', '8');
    axios.get(`${API}/analytics/opportunity-map?${p}`, { timeout: 35000 })
      .then((r) => setOutcomeTable(r.data?.outcome_table || [])).catch(() => setOutcomeTable([]));
  }, [agentId, domainId]);
  if (!indiv && !outcomeTable.length) return null;
  return (
    <div className="space-y-6" data-testid="coaching-charts-block">
      {indiv && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Score Trend</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={indiv.trend} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <RTooltip formatter={(v) => [`${v}`, 'Avg Score']} />
                <Line type="monotone" dataKey="avg_score" stroke="#09D6B4" strokeWidth={2} dot={{ r: 3, fill: '#09D6B4' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Skill Breakdown (%)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={indiv.skills} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis dataKey="skill" type="category" tick={{ fontSize: 10 }} width={100} />
                <RTooltip formatter={(v) => [`${v}%`, 'Met Rate']} />
                <Bar dataKey="score" name="Score" fill="#09D6B4" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {outcomeTable.length > 0 && <CoachingOutcomeTable rows={outcomeTable} />}
    </div>
  );
};

const STATUS_COLOR = {
  'Team Strength':      '#10B981',
  'Positive Momentum':  '#14B8A6',
  'Build Consistency':  '#818CF8',
  'Priority Focus':     '#F59E0B',
  'Compliance Risk':    '#DC2626',
};

const ProgressChip = ({ delta }) => {
  if (delta == null) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
        <span className="text-slate-400">—</span> first window
      </span>
    );
  }
  const positive = delta >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = positive ? '#10B981' : '#F59E0B';
  const abs = Math.abs(delta).toFixed(1);
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-semibold"
      style={{ color }}
    >
      <Icon className="w-3.5 h-3.5" />
      {positive ? `+${abs}` : `-${abs}`} pts this window
    </span>
  );
};

const ItemList = ({ items, iconColor, emptyMsg }) => {
  if (!items?.length) {
    return <p className="text-xs italic text-slate-400 py-1">{emptyMsg}</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((it) => (
        <li key={it.topic} className="flex items-start gap-2 text-sm">
          <span
            className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
            style={{ background: STATUS_COLOR[it.status] || iconColor }}
          />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-800 leading-tight">{it.topic}</p>
            <p className="text-[11px] text-slate-500 tabular-nums">
              {it.demonstrated_pct}% demonstrated · {it.opportunity_pct}% opportunity
              {it.trend_label && it.trend_label !== 'Stable' && (
                <span className="ml-1 text-slate-400">· {it.trend_label}</span>
              )}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
};

const _fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }); }
  catch { return iso.slice(0, 10); }
};

const PersonalNudgeCard = ({ nudge, isSupervisor }) => {
  const delta = nudge?.delta;
  let chipBg = '#F1F5F9';
  let chipColor = '#475569';
  let chipLabel = 'First window';
  if (delta != null) {
    if (delta >= 4) {
      chipBg = '#DCFCE7'; chipColor = '#166534'; chipLabel = `↑ ${delta} pts`;
    } else if (delta <= -4) {
      chipBg = '#FEF3C7'; chipColor = '#B45309'; chipLabel = `${delta} pts`;
    } else {
      chipBg = '#E2E8F0'; chipColor = '#334155';
      chipLabel = `${delta >= 0 ? '+' : ''}${delta} pts`;
    }
  }
  return (
    <div
      className="rounded-2xl border border-teal-100 bg-white p-5 shadow-sm"
      data-testid="my-coaching-personal-nudge"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="inline-flex items-center gap-2 text-sm font-bold text-slate-800">
          <MessageSquare className="w-4 h-4 text-teal-600" />
          {isSupervisor ? "This Agent's Coaching Nudge" : 'Your Coaching Nudge'}
        </div>
        <span
          className="text-xs font-bold px-2.5 py-1 rounded-full"
          style={{ background: chipBg, color: chipColor }}
        >
          {chipLabel}
        </span>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed">{nudge.progress_line}</p>
      <p className="text-sm text-slate-700 leading-relaxed mt-1">{nudge.focus_line}</p>
      <p
        className="text-sm italic mt-3 text-teal-700 border-l-2 border-teal-300 pl-3"
        data-testid="my-coaching-try-line"
      >
        {nudge.try_line}
      </p>
    </div>
  );
};

/**
 * <MyCoachingTab />
 *
 * Individual user-safe view per spec §10. Agents see themselves only;
 * supervisors get a picker at the top so they can browse the team without
 * ever landing on a failure leaderboard.
 */
const MyCoachingTab = ({ domainId = null }) => {
  const { user, token, startMasquerade } = useAuth();
  const navigate = useNavigate();
  const isSupervisor = ['master_admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor'].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [discTips, setDiscTips] = useState(null);
  const [refreshingTips, setRefreshingTips] = useState(false);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('days', '30');
    if (isSupervisor && selectedAgent) p.set('agent_id', selectedAgent);
    return p.toString();
  }, [domainId, isSupervisor, selectedAgent]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [coach, ags] = await Promise.all([
        axios.get(`${API}/analytics/my-coaching?${qs}`),
        isSupervisor
          ? axios.get(`${API}/analytics/my-coaching/agents${domainId ? `?domain_id=${domainId}` : ''}`)
          : Promise.resolve({ data: { agents: [] } }),
      ]);
      setData(coach.data);
      setAgents(ags.data?.agents || []);
    } catch (e) {
      toast.error('Could not load coaching view');
    } finally {
      setLoading(false);
    }
  }, [qs, isSupervisor, domainId]);

  useEffect(() => { load(); }, [load]);

  // Personalised DISC tips — lazy-loaded once data resolves (which
  // guarantees `user` is populated). Cached 24h server-side.
  const isAgent = !['master_admin', 'admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor'].includes(user?.role);
  useEffect(() => {
    if (!isAgent || isSupervisor || !data || data.no_data) return;
    axios.get(`${API}/analytics/my-coaching/disc-tips${domainId ? `?domain_id=${domainId}` : ''}`)
      .then((r) => setDiscTips(r.data))
      .catch(() => setDiscTips(null));
  }, [isAgent, isSupervisor, domainId, data]);

  const refreshDiscTips = async () => {
    setRefreshingTips(true);
    try {
      const r = await axios.post(
        `${API}/analytics/my-coaching/disc-tips/refresh${domainId ? `?domain_id=${domainId}` : ''}`,
      );
      setDiscTips(r.data);
      if (r.data?.rate_limited) {
        toast.info(`Refresh rate-limited — try again in ${r.data.retry_in_minutes}m.`);
      } else if (r.data?.empty) {
        toast.info('Not enough graded calls with a DISC profile yet — grade a few more first.');
      } else {
        toast.success('Personalised DISC tips refreshed.');
      }
    } catch {
      toast.error('Could not refresh DISC tips.');
    } finally {
      setRefreshingTips(false);
    }
  };

  // Supervisor "View as agent" — mint a short-lived impersonation token
  // and hand off to AuthContext so the whole app re-renders as that
  // agent (RBAC, dashboard, calls, everything). Restricted to agents
  // this supervisor is scoped to. See routes/admin.py::impersonate_user
  // for the backend guardrails.
  //
  // Client-side navigation (useNavigate) is CRITICAL here. A full-page
  // reload (window.location.href='/') would send the httpOnly cookie for
  // the ORIGINAL supervisor session to /auth/me on mount and revert us
  // straight back to the supervisor. Since the masquerade JWT is
  // in-memory only (see AuthContext), the reload wipes it. Keeping the
  // navigation client-side preserves the masqueraded token + user state.
  const viewAsSelectedAgent = async () => {
    const picked = agents.find(a => a.agent_id === selectedAgent);
    if (!picked?.user_id) {
      toast.error('This agent has no portal account yet — ask an admin to create one.');
      return;
    }
    try {
      const r = await axios.post(
        `${API}/admin/impersonate/${picked.user_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
      );
      startMasquerade(r.data.target, r.data.access_token);
      toast.success(`Viewing as ${r.data.target.name || r.data.target.email}`);
      // Navigate to the agent portal via client-side routing so the
      // masqueraded in-memory token survives.
      navigate('/my-domain');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start impersonation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!data?.enabled) {
    // Supervisors land here before picking an agent — still show the picker
    // so they can select someone to view. Regular agents just get the
    // "unavailable" message (no scope).
    if (isSupervisor) {
      return (
        <div className="space-y-6" data-testid="my-coaching-tab">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Agents Coaching</h2>
              <p className="text-xs text-slate-500 mt-1">
                Pick an agent to see their strengths, current focus and self-comparison.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700"
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                data-testid="my-coaching-agent-picker"
              >
                <option value="">— Select an agent —</option>
                {agents.map((a) => (
                  <option key={a.agent_id} value={a.agent_id}>
                    {a.agent_name} ({a.call_count} calls)
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={viewAsSelectedAgent}
                disabled={!selectedAgent}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-600 hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="my-coaching-view-as-agent"
                title="Start a short-lived session as this agent to see exactly what they see"
              >
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                View as agent
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Select an agent above to view their coaching view.
          </div>
        </div>
      );
    }
    return (
      <div className="text-center text-slate-400 italic py-12">
        Coaching view is unavailable for your account right now.
      </div>
    );
  }

  const title = isSupervisor ? "Agents Coaching" : "My Coaching";

  return (
    <div className="space-y-6" data-testid="my-coaching-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500 mt-1">
            {isSupervisor
              ? 'Pick an agent to see their strengths, current focus and self-comparison.'
              : 'Your strengths, your current focus, your progress — compared against your own previous window.'}
          </p>
        </div>
        {isSupervisor && (
          <div className="flex items-center gap-2">
            <select
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700"
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              data-testid="my-coaching-agent-picker"
            >
              <option value="">— Select an agent —</option>
              {agents.map((a) => (
                <option key={a.agent_id} value={a.agent_id}>
                  {a.agent_name} ({a.call_count} calls)
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={viewAsSelectedAgent}
              disabled={!selectedAgent}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-600 hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="my-coaching-view-as-agent"
              title="Start a short-lived session as this agent to see exactly what they see"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              View as agent
            </button>
          </div>
        )}
      </div>

      {data.no_data ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          {data.message}
        </div>
      ) : (
        <>
          {/* Header card */}
          <div className="rounded-2xl bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-100 p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-700">
                  {data.agent_name}
                </p>
                <p className="text-3xl font-bold text-slate-900 tabular-nums mt-1">
                  {data.current_avg_score ?? 0}<span className="text-lg text-slate-400">/100</span>
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  {data.total_calls} calls in the last {data.days} days
                </p>
              </div>
              <div className="text-right space-y-1">
                <ProgressChip delta={data.progress_delta} />
                {data.previous_avg_score != null && (
                  <p className="text-[11px] text-slate-500 tabular-nums">
                    previous window: {data.previous_avg_score}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Personal Coaching Nudge — same 3-line talking track that goes
              into the weekly email, surfaced here so agents see it daily. */}
          {data.personal_nudge && (
            <PersonalNudgeCard nudge={data.personal_nudge} isSupervisor={isSupervisor} />
          )}

          {/* DISC Strengths — where this agent closes best per the
              recommendation matrix. Silent when no strong cohorts yet. */}
          {(data.disc_strengths?.length > 0 || data.revivable_routed_away_count > 0) && (
            <div
              className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4"
              data-testid="disc-strengths-card"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 mt-0.5 text-indigo-600 shrink-0" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-bold text-indigo-900">Your Personality-Match Edge</p>
                  {data.disc_strengths?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {data.disc_strengths.map((s) => (
                        <div
                          key={s.disc_profile}
                          className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white border border-indigo-200 text-xs"
                          data-testid={`disc-strength-${s.disc_profile}`}
                        >
                          <span className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                                style={{ background: { D: '#EF4444', I: '#F59E0B', S: '#10B981', C: '#3B82F6' }[s.disc_profile] || '#94A3B8' }}>
                            {s.disc_profile}
                          </span>
                          <span className="font-semibold text-slate-800">
                            {Math.round((s.raw_win_rate || 0) * 100)}% close
                          </span>
                          <span className="text-slate-500">n={s.sample_size}</span>
                          {s.team_base_rate != null && (
                            <span className="text-emerald-600 font-semibold">
                              +{Math.round(((s.win_rate || 0) - (s.team_base_rate || 0)) * 100)} vs team
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-indigo-700 italic">
                      Not enough matched-personality data yet — keep taking calls and your edge will appear here.
                    </p>
                  )}
                  {data.revivable_routed_away_count > 0 && (
                    <p className="text-xs text-slate-600">
                      <strong>{data.revivable_routed_away_count}</strong>{' '}
                      revivable {data.revivable_routed_away_count === 1 ? 'deal was' : 'deals were'} routed
                      to a peer this window based on the best-fit engine. Ask your manager which personality
                      types you can build depth on next.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Personalised DISC selling tips — LLM-generated from THIS
              agent's own recent caller quotes. Rate-limited to 1/hour. */}
          {isAgent && discTips && !discTips.empty && (
            <div className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50/50 to-white p-4" data-testid="personal-disc-tips-card">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-teal-600" />
                <p className="text-sm font-bold text-teal-900">
                  Your Personalised DISC Playbook
                </p>
                <span className="text-[10px] text-slate-500 italic">
                  Based on {discTips.sample_size || 0} of your own caller quotes
                </span>
                <button
                  type="button"
                  onClick={refreshDiscTips}
                  disabled={refreshingTips}
                  className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900 disabled:opacity-50"
                  data-testid="refresh-disc-tips-btn"
                  title="Regenerate from your last 6 graded calls (limit: once per hour)"
                >
                  {refreshingTips ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {refreshingTips ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(discTips.tips_by_disc || {}).map(([disc, tips]) => (
                  <div
                    key={disc}
                    className="rounded-lg bg-white border border-slate-200 p-3"
                    data-testid={`disc-tips-${disc}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ background: { D: '#EF4444', I: '#F59E0B', S: '#10B981', C: '#3B82F6' }[disc] || '#94A3B8' }}
                      >
                        {disc}
                      </span>
                      <span className="text-xs font-semibold text-slate-700">
                        Selling to DISC · {disc}
                      </span>
                    </div>
                    <ul className="space-y-1.5">
                      {(tips || []).map((tip, i) => (
                        <li key={i} className="text-[11px] text-slate-700 leading-snug pl-3 border-l-2 border-slate-200">
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {discTips.llm_error && (
                <p className="text-[10px] text-amber-700 italic mt-2">
                  Personalised generation partial — using deterministic tips for now. Try refreshing later.
                </p>
              )}
              <p className="text-[10px] text-slate-400 italic mt-2">
                Regenerates from your last {6} graded calls with a DISC profile. Cached 24h · limit 1 refresh/hour.
              </p>
            </div>
          )}
          {isAgent && discTips?.empty && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600" data-testid="personal-disc-tips-empty">
              Personalised DISC tips will appear here once you have a few graded calls with a DISC profile.
            </div>
          )}

          {/* Personal gamification — XP, badges, reflection, kudos */}
          <MyGamificationCard domainId={domainId} agentId={data.agent_id} />

          {/* Strengths + Focus grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-emerald-100 rounded-xl p-5">
              <h3 className="text-sm font-bold text-emerald-800 uppercase tracking-wider mb-3 inline-flex items-center gap-2">
                <Award className="w-4 h-4" /> Your Strengths
              </h3>
              <ItemList
                items={data.strengths}
                iconColor="#10B981"
                emptyMsg="Strengths appear once you consistently demonstrate a coaching area (90%+)."
              />
            </div>
            <div className="bg-white border border-amber-100 rounded-xl p-5">
              <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wider mb-3 inline-flex items-center gap-2">
                <Target className="w-4 h-4" /> Your Current Coaching Focus
              </h3>
              <ItemList
                items={data.coaching_focus}
                iconColor="#F59E0B"
                emptyMsg="No coaching focus right now — you&apos;re demonstrating every area consistently."
              />
            </div>
          </div>

          {/* Score Trend + Skill Breakdown — moved here from the
              Individual tab (Sep-2026 spec): coaching detail lives with
              coaching context, directly under Your Current Coaching Focus. */}
          <CoachingChartsBlock agentId={data.agent_id} domainId={domainId} />

          {/* Suggested next action */}
          <div className="rounded-xl border border-teal-200 bg-teal-50 p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 mt-0.5 text-teal-600 shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-bold text-teal-900">Suggested Next Action</p>
                <p className="text-sm text-teal-800">{data.suggested_next_action}</p>
              </div>
            </div>
          </div>

          {/* Example snippets */}
          {data.example_snippets?.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 inline-flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-slate-500" /> Example moments to look at
              </h3>
              <div className="space-y-2">
                {data.example_snippets.map((s, i) => (
                  <div
                    key={`${s.call_id}-${i}`}
                    className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700"
                  >
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                      <span className="font-semibold">{s.topic}</span>
                      <span className="tabular-nums">{_fmtDate(s.date)}</span>
                    </div>
                    <p className="italic">&ldquo;{s.snippet}&rdquo;</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyCoachingTab;
